import { useRouter } from 'next/router';
import React, { type FC, useEffect } from 'react';

import Buttons from '@/components/Buttons';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import CurrencyCircleDollar from '@/components/Icons/CurrencyCircleDollar';
import * as format from '@/format';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { type Creator, type Price, type Product } from '@/typings/api-models';
import { type Asset, type ProductType } from '@/typings/graphql-models';

import s from './PaymentLinkButton.module.scss';

type Props = {
  data: {
    price?: Pick<Price, 'id' | 'priceTitle'> & {
      product?: Pick<Product, 'id' | 'slug' | 'isActive' | 'title'> & {
        productTypes?: Pick<ProductType, 'title' | 'noodleProductType'>[];
        creator?: Pick<Creator, 'id' | 'slug' | 'name'> & {
          image?: Pick<Asset, 'url'> | null;
        };
      };
    };
    workflowReferenceId?: string;
    // TODO: receive the isCompleted from the backend
    isCompleted?: boolean;
  };
  canClick: boolean;
  setIsFulfilled?: (f: boolean) => void;
  completedAt: string | null;
};

const PaymentLinkButton: FC<Props> = ({ setIsFulfilled, canClick, data, completedAt }) => {
  const router = useRouter();

  const creatorSlug = data.price?.product?.creator?.slug;
  const productSlug = data.price?.product?.slug;
  const priceId = data.price?.id;
  const title = data.price?.priceTitle || data.price?.product?.title;

  useEffect(() => {
    if (data) {
      setIsFulfilled?.(data.isCompleted ?? false);
    }
  }, [data, setIsFulfilled]);

  if (!creatorSlug || !productSlug || !priceId) {
    return null;
  }

  return (
    <Buttons
      className={s.container}
      disabled={!canClick}
      isFullWidth
      isWrapper
      onClick={() => {
        router.push(
          getUrl(IDENTIFIERS.PRODUCT_PURCHASE, {
            creatorSlug,
            priceId,
            productSlug,
            workflowReferenceId: data.workflowReferenceId,
          }),
        );
      }}
    >
      {data.isCompleted ? (
        <CheckCircle size={16} weight='fill' color='var(--color-success)' />
      ) : (
        <figure>
          <CurrencyCircleDollar weight='fill' size={20} />
        </figure>
      )}
      <div>
        <p
          className={data.isCompleted ? 'caption-bold' : 'body-sm-bold'}
          style={data.isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
        >
          Buy {title}
        </p>
        {completedAt && (
          <p className='caption'>Completed on {format.datetimeLocalTZ.withoutTimezone({ datetime: completedAt })}</p>
        )}
      </div>
      {canClick && (
        <>
          {data.isCompleted ? (
            <CaretRight size={12} color='var(--color-gray-100)' />
          ) : (
            <div
              className={s.open}
              {...(!data.isCompleted && {
                style: {
                  backgroundColor: 'var(--color-primary)',
                  border: '1px solid var(--color-primary)',
                  color: 'var(--color-gray-0)',
                },
              })}
            >
              View
            </div>
          )}
        </>
      )}
    </Buttons>
  );
};

export default PaymentLinkButton;
